import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import styles from './GradeInput.module.scss';
import { menuSurface, menuListSurface } from '../../utils/selectMenuStyles';

// Touch-device detection: iPhone/Android open a native `<select>` as the
// familiar spinning wheel / bottom-sheet picker. Desktop keeps react-
// select for the type-to-filter behavior. `pointer: coarse` catches
// touchscreens (phones, tablets) without misfiring on a mouse-connected
// laptop with a touchscreen.
function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    setIsTouch(window.matchMedia('(pointer: coarse)').matches);
  }, []);
  return isTouch;
}

// `label` is the historical/internal spelling shown to admins.
// `shortLabel` is the customer-facing form ("1st Grade", "10th Grade", ...)
// that scans faster on the "look up your access code" flow. Only the label
// changes -- the stored `value` is identical so nothing internal shifts.
const ALL_GRADES = [
  { label: 'Staff', shortLabel: 'Staff', value: 'STAFF' },
  { label: 'Preschool', shortLabel: 'Preschool', value: 'PS' },
  { label: 'Transitional Kindergarten', shortLabel: 'Transitional Kindergarten', value: 'TK' },
  { label: 'Kindergarten', shortLabel: 'Kindergarten', value: '0' },
  { label: 'First Grade', shortLabel: '1st Grade', value: '1' },
  { label: 'Second Grade', shortLabel: '2nd Grade', value: '2' },
  { label: 'Third Grade', shortLabel: '3rd Grade', value: '3' },
  { label: 'Fourth Grade', shortLabel: '4th Grade', value: '4' },
  { label: 'Fifth Grade', shortLabel: '5th Grade', value: '5' },
  { label: 'Sixth Grade', shortLabel: '6th Grade', value: '6' },
  { label: 'Seventh Grade', shortLabel: '7th Grade', value: '7' },
  { label: 'Eighth Grade', shortLabel: '8th Grade', value: '8' },
  { label: 'Freshman', shortLabel: '9th Grade', value: '9' },
  { label: 'Sophomore', shortLabel: '10th Grade', value: '10' },
  { label: 'Junior', shortLabel: '11th Grade', value: '11' },
  { label: 'Senior', shortLabel: '12th Grade', value: '12' },
  { label: 'Other', shortLabel: 'Other', value: null },
];

// `error`       — apply a red control border + halo (matches SchoolInput).
// `placeholder` — react-select empty-state text.
// `short`       — render the customer-facing labels ("1st Grade" etc.)
//                 instead of the historical spelled-out ones ("First
//                 Grade"). Values are unchanged either way -- this only
//                 affects the display label.
// Same 48px-tall, 10px-radius, brand-blue focus dressing as SchoolInput so
// the two dropdowns feel like the same species of control as our TextInputs.
const GradeInput = ({
  val = '', onChange, required = false, availableGrades = [], error = false, placeholder, short = false, menuPlacement = 'auto',
}) => {
  const isTouch = useIsTouchDevice();

  // Always one of these 16 options -- a school's own codes ("HS", "13",
  // "Bronco 11/12") are never offered verbatim.
  //
  // `availableGrades` narrows the list to what the school actually has students
  // in, already folded onto these codes by Student.canonical_grade. The fold
  // matters: a school storing "K" has no literal "0" on any row, and without it
  // the filter removed Kindergarten from that school's dropdown entirely.
  //
  // Falling back to the full list when the filter empties the options is not
  // defensive padding -- a school whose grades ALL sit outside this list would
  // otherwise render an empty required dropdown, and the parent could not
  // submit the form at all.
  const narrowed = ALL_GRADES.filter((grade) => availableGrades.includes(grade.value));
  const filteredGrades = narrowed.length > 0 ? narrowed : ALL_GRADES;
  const gradeOptions = filteredGrades.map((g) => ({ value: g.value, label: short ? g.shortLabel : g.label }));

  // Native <select> path for phones/tablets. `value=""` (empty string)
  // stands in for "no selection" because <option value="null"> would be
  // set as the literal string "null" -- STAFF/PS/TK/0-12 and null are all
  // real grade values, so we index the options by their POSITION when
  // reading back what the user picked.
  if (isTouch) {
    const currentIdx = filteredGrades.findIndex((g) => g.value === val);
    return (
      <select
        className={`${styles.nativeSelect} ${error ? styles.nativeSelectError : ''} ${currentIdx < 0 ? styles.nativeSelectPlaceholder : ''}`}
        value={currentIdx < 0 ? '' : String(currentIdx)}
        onChange={(e) => {
          const idx = parseInt(e.target.value, 10);
          if (Number.isNaN(idx)) return;
          onChange(filteredGrades[idx].value);
        }}
      >
        {placeholder && (
          <option value="" disabled>{placeholder}</option>
        )}
        {filteredGrades.map((g, idx) => (
          <option key={idx} value={String(idx)}>
            {short ? g.shortLabel : g.label}
          </option>
        ))}
      </select>
    );
  }

  const controlBase = {
    minHeight: '48px',
    borderRadius: '10px',
    borderColor: '#e2e8f0',
    fontSize: '16px',
  };
  const customStyles = {
    // Selected = light brand tint; focused/hover = soft grey. React-select's
    // default #2684FF was too shouty and washed out the label. The `:active`
    // override matches focus so clicks don't briefly flash the dark blue.
    // `whiteSpace: nowrap` keeps long labels like "Transitional Kindergarten"
    // on one line -- paired with the menu style below, the menu widens to
    // the widest option instead of wrapping.
    option: (provided, state) => ({
      ...provided,
      color: '#0f172a',
      backgroundColor: state.isSelected
        ? '#e0f2fe'
        : state.isFocused
          ? '#f1f5f9'
          : 'white',
      fontFamily: 'inherit',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      ':active': { backgroundColor: '#e0f2fe' },
    }),
    // Rounded to match the control (shared with SchoolInput so the two
    // dropdowns on the same form can't drift), then allowed to grow to fit the
    // widest option (so "Transitional Kindergarten" stays on one line) without
    // ever shrinking below the control's width.
    menu: (provided) => ({
      ...menuSurface(provided),
      width: 'max-content',
      minWidth: '100%',
    }),
    menuList: menuListSurface,
    singleValue: (provided) => ({
      ...provided,
      color: '#0f172a',
      fontFamily: 'inherit',
      fontSize: '16px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#94a3b8',
      fontSize: '16px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    control: (provided, state) => {
      const base = { ...provided, ...controlBase };
      if (error) {
        return {
          ...base,
          borderColor: '#dc2626',
          boxShadow: '0 0 0 3px rgba(220, 38, 38, 0.15)',
          '&:hover': { borderColor: '#dc2626' },
        };
      }
      if (state.isFocused) {
        return {
          ...base,
          borderColor: '#008fcf',
          boxShadow: '0 0 0 3px rgba(0, 143, 207, 0.18)',
          '&:hover': { borderColor: '#008fcf' },
        };
      }
      return { ...base, boxShadow: 'none', '&:hover': { borderColor: '#cbd5e1' } };
    },
    input: (provided) => ({
      ...provided,
      background: 'transparent',
      boxShadow: 'none !important',
      color: '#0f172a',
    }),
  };

  const currentOption = ALL_GRADES.find((grade) => grade.value === val);

  return (
    <Select
      value={currentOption ? { value: currentOption.value, label: short ? currentOption.shortLabel : currentOption.label } : null}
      options={gradeOptions}
      onChange={(opt) => onChange(opt.value)}
      placeholder={placeholder}
      styles={customStyles}
      menuPlacement={menuPlacement}
    />
  );
};

GradeInput.propTypes = {
  val: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  availableGrades: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.bool,
  placeholder: PropTypes.string,
  short: PropTypes.bool,
  menuPlacement: PropTypes.oneOf(['auto', 'top', 'bottom']),
};

export default GradeInput;
